import { TEAM_ROLE_PERMISSIONS } from '../constants';
import { useMemo } from 'react';

/**
 * Hook to check team-based permissions based on user's role in the client.
 * 
 * Roles & Permissions:
 * | Role    | VIEW_JOBS | EDIT_JOBS | VIEW_BILLING | MANAGE_TEAM | TRANSFER_OWNERSHIP |
 * |---------|-----------|-----------|--------------|-------------|-------------------|
 * | owner   | ✅        | ✅        | ✅           | ✅          | ✅                |
 * | admin   | ✅        | ✅        | ✅           | ✅          | ❌                |
 * | user    | ✅        | ✅        | ❌           | ❌          | ❌                |
 * | viewer  | ✅        | ❌        | ❌           | ❌          | ❌                |
 */

const useTeamPermissions = (client) => {
  const myRole = useMemo(() => client?.myRole, [client]);
  const result = useMemo(() => {
    const permissions = myRole ? TEAM_ROLE_PERMISSIONS[myRole] || [] : [];
    return {
      // Permission checks
      canViewJobs: permissions.includes('VIEW_JOBS'),
      canEditJobs: permissions.includes('EDIT_JOBS'),
      canViewBilling: permissions.includes('VIEW_BILLING'),
      canManageTeam: permissions.includes('MANAGE_TEAM'),
      canTransferOwnership: permissions.includes('TRANSFER_OWNERSHIP'),

      // Role checks
      isOwner: myRole === 'owner',
      isAdmin: myRole === 'admin',
      isUser: myRole === 'user',
      isViewer: myRole === 'viewer',

      // Current role
      myRole,

      // Helper to check if user can modify another user's role
      canModifyMember: (memberRole) => {
        if (!myRole) return false;
        if (memberRole === 'owner') return false; // Cannot modify owner
        if (myRole === 'owner') return true; // Owner can modify anyone except themselves
        if (myRole === 'admin') {
          // Admin can only modify users and viewers
          return ['user', 'viewer'].includes(memberRole);
        }
        return false;
      },

      // Helper to check if user can promote to a specific role
      canPromoteTo: (targetRole) => {
        if (!myRole) return false;
        if (targetRole === 'owner') return false; // Use transferOwnership instead
        if (myRole === 'owner') return true; // Owner can promote to any role
        if (myRole === 'admin') {
          // Admin can only set user/viewer roles
          return ['user', 'viewer'].includes(targetRole);
        }
        return false;
      },
    };
  }, [myRole]);
  // console.log("useTeamPermissions result:", result, "for role:", myRole);
  return result;
};

export default useTeamPermissions;
